import {
  DatePipe,
  HashLocationStrategy,
  LocationStrategy,
} from '@angular/common'
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http'
import { ErrorHandler, NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { BlockUIModule } from 'ng-block-ui'
import { BlockUIHttpModule } from 'ng-block-ui/http'
import { NgxSpinnerService } from 'ngx-spinner'
import { AccordionModule } from 'primeng/accordion'
import { ConfirmationService, MessageService } from 'primeng/api'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { BreadcrumbModule } from 'primeng/breadcrumb'
import { ButtonModule } from 'primeng/button'
import { CalendarModule } from 'primeng/calendar'
import { CardModule } from 'primeng/card'
import { CarouselModule } from 'primeng/carousel'
import { ChartModule } from 'primeng/chart'
import { CheckboxModule } from 'primeng/checkbox'
import { ChipsModule } from 'primeng/chips'
import { ColorPickerModule } from 'primeng/colorpicker'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { ContextMenuModule } from 'primeng/contextmenu'
import { DialogModule } from 'primeng/dialog'
import { DropdownModule } from 'primeng/dropdown'
import { EditorModule } from 'primeng/editor'
import { FieldsetModule } from 'primeng/fieldset'
import { FileUploadModule } from 'primeng/fileupload'
import { GalleriaModule } from 'primeng/galleria'
import { InplaceModule } from 'primeng/inplace'
import { InputMaskModule } from 'primeng/inputmask'
import { InputSwitchModule } from 'primeng/inputswitch'
import { InputTextModule } from 'primeng/inputtext'
import { InputTextareaModule } from 'primeng/inputtextarea'
import { ListboxModule } from 'primeng/listbox'
import { MegaMenuModule } from 'primeng/megamenu'
import { MenuModule } from 'primeng/menu'
import { MenubarModule } from 'primeng/menubar'
import { MessageModule } from 'primeng/message'
import { MessagesModule } from 'primeng/messages'
import { MultiSelectModule } from 'primeng/multiselect'
import { OrderListModule } from 'primeng/orderlist'
import { OrganizationChartModule } from 'primeng/organizationchart'
import { OverlayPanelModule } from 'primeng/overlaypanel'
import { PaginatorModule } from 'primeng/paginator'
import { PanelModule } from 'primeng/panel'
import { PanelMenuModule } from 'primeng/panelmenu'
import { PasswordModule } from 'primeng/password'
import { PickListModule } from 'primeng/picklist'
import { ProgressBarModule } from 'primeng/progressbar'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { RadioButtonModule } from 'primeng/radiobutton'
import { RatingModule } from 'primeng/rating'
import { ScrollPanelModule } from 'primeng/scrollpanel'
import { SelectButtonModule } from 'primeng/selectbutton'
import { SlideMenuModule } from 'primeng/slidemenu'
import { SliderModule } from 'primeng/slider'
import { SpinnerModule } from 'primeng/spinner'
import { SplitButtonModule } from 'primeng/splitbutton'
import { StepsModule } from 'primeng/steps'
import { TableModule } from 'primeng/table'
import { TabMenuModule } from 'primeng/tabmenu'
import { TabViewModule } from 'primeng/tabview'
import { TerminalModule } from 'primeng/terminal'
import { TieredMenuModule } from 'primeng/tieredmenu'
import { ToggleButtonModule } from 'primeng/togglebutton'
import { ToolbarModule } from 'primeng/toolbar'
import { TooltipModule } from 'primeng/tooltip'
import { TreeModule } from 'primeng/tree'
import { TreeTableModule } from 'primeng/treetable'
import { environment } from '../environments/environment'
import { ApiModule } from './api/api.module'
import { AppComponent } from './app.component'
import { AppMainComponent } from './app.main.component'
import { AppInlineProfileComponent } from './app.profile.component'
import { AppRoutes } from './app.routes'
import { FormErrorMessagesDirective } from './carefinity/directives/FormErrorMessagesDirective'
import { JwtInterceptor } from './carefinity/services/jwt.interceptor'
import { AppLoginComponent } from './carefinity/views/login/app.login.component'
import { EditNoteComponent } from './carefinity/views/manage-notes/edit/edit-note.component'
import { ManageQuestionsComponent } from './carefinity/views/manage-questions/manage-questions.component'
import { ResetPasswordComponent } from './carefinity/views/password-reset/reset-password/reset-password.component'
import { ResetUserPasswordComponent } from './carefinity/views/password-reset/reset-user-password/reset-user-password.component'
import { PatientListComponent } from './carefinity/views/patient/patient-list/patient-list.component'
import { QuestionAnswerComponent } from './carefinity/views/question-answer/question-answer.component'
import { SelectPracticeComponent } from './carefinity/views/select-practice/select-practice.component'
import { AppBreadcrumbComponent } from './carefinity/views/shared-components/app.breadcrumb.component'
import { AppFooterComponent } from './carefinity/views/shared-components/app.footer.component'
import { AppMenuComponent } from './carefinity/views/shared-components/app.menu.component'
import { BreadcrumbService } from './carefinity/views/shared-components/breadcrumb.service'
import { EmedHeaderComponent } from './carefinity/views/shared-components/emed-header/emed-header.component'
import { CreateUserComponent } from './carefinity/views/user/create/create-user.component'
import { UserListComponent } from './carefinity/views/user/list/user-list.component'
import { UserPracticesComponent } from './carefinity/views/userpractices/user-practices.component'
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component'
import { AppErrorComponent } from './pages/app.error.component'
import { AppNotfoundComponent } from './pages/app.notfound.component'

import { ScrollingDirective } from './carefinity/directives/ScrollingDirective'
import { EmergencyTokenComponent } from './carefinity/views/global-settings/emergency-token/emergency-token.component'
import { PatientDemographicsComponent } from './carefinity/views/global-settings/patient-demographics/patient-demographics.component'
import { AddNotesComponent } from './carefinity/views/manage-notes/add/add-notes.component'
import { ManageNotesComponent } from './carefinity/views/manage-notes/list/manage-notes.component'
import { PatientArchiveComponent } from './carefinity/views/patient-archive/patient-archive.component'

import { AppointmentsListComponent } from './carefinity/views/appointments/appointments-list/appointments-list.component'
import { DecryptconnectionStringsComponent } from './carefinity/views/global-settings/decryptconnection-strings/decryptconnection-strings.component'
import { AddReportsComponent } from './carefinity/views/reports/add/addReports.component'
import { EditReportComponent } from './carefinity/views/reports/edit/edit-report.component'
import { ListReportComponent } from './carefinity/views/reports/list/list-reports.component'
import { ReportparamAddComponent } from './carefinity/views/reports/report-param/reportparam-add/reportparam-add.component'
import { ReportparamEditComponent } from './carefinity/views/reports/report-param/reportparam-edit/reportparam-edit.component'
import { ReportparamListComponent } from './carefinity/views/reports/report-param/reportparam-list/reportparam-list.component'
import { ReportsCategoryComponent } from './carefinity/views/reports/reports-category/reports-category.component'
//import { TasksListComponent } from './carefinity/views/tasks/tasks-list/tasks-list.component';
import { Customsort } from './carefinity/directives/customsort'
import { AllergyListComponent } from './carefinity/views/patient/allergy-list/allergy-list.component'
import { BillingListComponent } from './carefinity/views/patient/billing/billing-list/billing-list.component'
import { DashboardComponent } from './carefinity/views/patient/dashboard/dashboard.component'
import { CatDocumentComponent } from './carefinity/views/patient/document/cat-document/cat-document.component'
import { DocumentListComponent } from './carefinity/views/patient/document/document-list/document-list.component'
import { ExportDocumentComponent } from './carefinity/views/patient/export-document/export-document.component'
import { FinancialDetailsComponent } from './carefinity/views/patient/financial/financial-details/financial-details.component'
import { FinancialListComponent } from './carefinity/views/patient/financial/financial-list/financial-list.component'
import { ListComponent } from './carefinity/views/patient/images/list/list.component'
import { LabDetailsComponent } from './carefinity/views/patient/lab-list/lab-details/lab-details.component'
import { LabListComponent } from './carefinity/views/patient/lab-list/lab-list.component'
import { MedicationListComponent } from './carefinity/views/patient/medication-list/medication-list.component'
import { NotesComponent } from './carefinity/views/patient/notes/notes.component'
import { OrderListComponent } from './carefinity/views/patient/order/order-list/order-list.component'
import { ProblemListComponent } from './carefinity/views/patient/problem-list/problem-list.component'
import { SigListComponent } from './carefinity/views/patient/significantEvents/sig-list/sig-list.component'
import { VisitsListComponent } from './carefinity/views/patient/visits-list/visits-list.component'
import { VitalListComponent } from './carefinity/views/patient/vitals/vital-list/vital-list.component'
import { PatientInfoComponent } from './carefinity/views/shared-components/patient-info/patient-info.component'
//import { SignoffListComponent } from './carefinity/views/signoff/signoff-list/signoff-list.component';
import { FormioModule } from '@formio/angular'
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer'
import { InfiniteScrollModule } from 'ngx-infinite-scroll'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputOtpModule } from 'primeng/inputotp'
import { SidebarModule } from 'primeng/sidebar'
import { SplitterModule } from 'primeng/splitter'
import { ToastModule } from 'primeng/toast'
import { VirtualScrollerModule } from 'primeng/virtualscroller'
import { AddFormAttributesDirective } from './carefinity/directives/AddFormAttributesDirective'
import { PrivilegePipe } from './carefinity/directives/Privelleges'
import { ErrorPopupComponent } from './carefinity/popups/errorpopup.component'
import { ErrorInterceptor } from './carefinity/services/ErrorInterceptor'
import { GlobalErrorHandler } from './carefinity/services/GlobalErrorHandler'
import { AuthGuard } from './carefinity/services/auth-guard'
import { CellverificationComponent } from './carefinity/views/Twofactor/cellverification/cellverification.component'
import { ChangePasswordComponent } from './carefinity/views/forgot-password/change-password/change-password.component'
import { EnterTokenComponent } from './carefinity/views/forgot-password/enter-token/enter-token.component'
import { ForceChangePasswordComponent } from './carefinity/views/forgot-password/force-password-change/force-password-change.component'
import { QuestionListComponent } from './carefinity/views/forgot-password/question-list/question-list.component'
import { RolesListComponent } from './carefinity/views/manage-roles/roles-list/roles-list.component'
import { RestorePatientComponent } from './carefinity/views/patient/restore-patient/restore-patient.component'
import { ReportViewComponent } from './carefinity/views/reports/report-view/report-view.component'
import { ReportsViewListComponent } from './carefinity/views/reports/reports-view-list/reports-view-list.component'
import { ReportsViewParamsComponent } from './carefinity/views/reports/reports-view-params/reports-view-params.component'
import { AppConfigComponent } from './carefinity/views/shared-components/app.config.component'
import { MenuService } from './carefinity/views/shared-components/app.menu.service'
import { AppMenuitemComponent } from './carefinity/views/shared-components/app.menuitem.component'
import { AppRightMenuComponent } from './carefinity/views/shared-components/app.rightmenu.component'
import { AppTopBarComponent } from './carefinity/views/shared-components/app.topbar.component'
import { ManagerolesComponent } from './carefinity/views/user/manageroles/manageroles.component'
import { UserprofileComponent } from './carefinity/views/user/userprofile/userprofile.component'
import { AppMFAComponent } from './pages/app.mfa.component'
import { AppSamlAuthenticationComponent } from './pages/app.samlAuthentication.component'
import { RegisterComponent } from './pages/register/register.component'
import { SesssionTimedComponent } from './pages/timedout.component'
import { PdfViewerComponent } from './pdf-viewer/pdf-viewer.component'
import { AppSettingsComponent } from './carefinity/views/app-settings/app-settings.component'
import { PracticeSettingsComponent } from './carefinity/views/practice-settings/practice-settings.component'
import { ApplicationConfigComponent } from './carefinity/views/application-config/application-config.component'
import { MaintainencePageComponent } from './carefinity/views/maintainence-page/maintainence-page.component'

import 'chartjs-adapter-moment'
import { GlobalMappingSettingComponent } from './carefinity/views/global-mapping-setting/global-mapping-setting.component'
import { CatImageComponent } from './carefinity/views/patient/images/cat-image/cat-image.component'
import { ImagesWrapperComponent } from './carefinity/views/patient/images/images-wrapper/images-wrapper.component'
import { ExportClinicalDataComponent } from './carefinity/views/patient/export-clinical-data/export-clinical-data.component'
import { TimelineComponent } from './timeline/timeline.component'
import { ApiKeysComponent } from './carefinity/views/api-keys/api-keys.component'
import { UserPreferenceSettingComponent } from './carefinity/views/user-preference-setting/user-preference-setting.component'
import { Time12HourPipe } from './helpers/time12-hour-pipe.pipe'
import { AddInsuranceComponent } from './carefinity/views/patient/add-insurance/add-insurance.component'
import { PatientDemographicComponent } from './carefinity/views/patient/patient-demographic/patient-demographic.component'
import { AppMainComponentSSO } from './app.main.componentSSO'

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutes,
    HttpClientModule,
    BrowserAnimationsModule,
    AccordionModule,
    AutoCompleteModule,
    BreadcrumbModule,
    ButtonModule,
    CalendarModule,
    CardModule,
    CarouselModule,
    ChartModule,
    CheckboxModule,
    ChipsModule,
    ConfirmDialogModule,
    ColorPickerModule,
    ContextMenuModule,
    DialogModule,
    DropdownModule,
    EditorModule,
    FieldsetModule,
    FileUploadModule,
    CalendarModule,
    GalleriaModule,
    InplaceModule,
    InputMaskModule,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    ListboxModule,
    MegaMenuModule,
    MenuModule,
    MenubarModule,
    MessageModule,
    MessagesModule,
    MultiSelectModule,
    OrderListModule,
    OrganizationChartModule,
    OverlayPanelModule,
    PaginatorModule,
    PanelModule,
    PanelMenuModule,
    PasswordModule,
    PickListModule,
    ProgressBarModule,
    RadioButtonModule,
    RatingModule,
    ScrollPanelModule,
    SelectButtonModule,
    SlideMenuModule,
    SliderModule,
    SpinnerModule,
    SplitButtonModule,
    StepsModule,
    TableModule,
    TabMenuModule,
    TabViewModule,
    TerminalModule,
    TieredMenuModule,
    ToastModule,
    ToggleButtonModule,
    ToolbarModule,
    TooltipModule,
    TreeModule,
    TreeTableModule,
    VirtualScrollerModule,
    BlockUIModule.forRoot(),
    BlockUIHttpModule.forRoot({
        requestFilters: [/echo/, /AppPreferences/,/getPendingDocuments/],
    }),
    ApiModule.forRoot({
      rootUrl:
        window.location.port == '4200'
          ? environment.rootUrl
          : `${window.location.protocol}//${window.location.hostname}:${window.location.port}${document.querySelector('base').getAttribute('href') == '/' ? '' : document.querySelector('base').getAttribute('href').slice(0, -1)}`,
    }),

    FormioModule,
    ProgressSpinnerModule,
    SidebarModule,
    SplitterModule,
    InputOtpModule,
    NgxExtendedPdfViewerModule,
    InfiniteScrollModule,
    FloatLabelModule,
  ],
  declarations: [
      AppComponent,
      AppMainComponentSSO,
    AppMainComponent,
    AppMenuComponent,
    AppMenuitemComponent,
    AppTopBarComponent,
    AppFooterComponent,
    AppBreadcrumbComponent,
    AppInlineProfileComponent,
    UserListComponent,
    AppNotfoundComponent,
    AppErrorComponent,
    AppAccessdeniedComponent,
    AppLoginComponent,
    CreateUserComponent,
    UserPracticesComponent,
    ResetPasswordComponent,
    ResetUserPasswordComponent,
    EditNoteComponent,
    ManageNotesComponent,
    ManageQuestionsComponent,
    FormErrorMessagesDirective,
    PatientListComponent,
    QuestionAnswerComponent,
    EmedHeaderComponent,
    SelectPracticeComponent,
    EmergencyTokenComponent,
    PatientDemographicsComponent,
    PatientArchiveComponent,
    DecryptconnectionStringsComponent,
    ReportsCategoryComponent,
    AddNotesComponent,
    ScrollingDirective,
    ListReportComponent,
    AddReportsComponent,
    EditReportComponent,
    ReportparamListComponent,
    ReportparamAddComponent,
    ReportparamEditComponent,
    AppointmentsListComponent,
    //TasksListComponent,
    PatientInfoComponent,
    AllergyListComponent,
    DashboardComponent,
    ProblemListComponent,
    MedicationListComponent,
    VisitsListComponent,
    OrderListComponent,
    LabListComponent,
    LabDetailsComponent,
    SigListComponent,
    FinancialListComponent,
    FinancialDetailsComponent,
    BillingListComponent,
    VitalListComponent,
    ExportDocumentComponent,
    ListComponent,
    DocumentListComponent,
    CatDocumentComponent,
    NotesComponent,
    //SignoffListComponent,
    AppRightMenuComponent,
    AppConfigComponent,
    AddFormAttributesDirective,
    ManagerolesComponent,
    PrivilegePipe,
    RolesListComponent,
    UserprofileComponent,
    QuestionListComponent,
    EnterTokenComponent,
    ChangePasswordComponent,
    CellverificationComponent,
    AppMFAComponent,
    AppSamlAuthenticationComponent,
    RegisterComponent,
    PdfViewerComponent,
    ForceChangePasswordComponent,
    ReportsViewListComponent,
    SesssionTimedComponent,
    ErrorPopupComponent,
    RestorePatientComponent,
    ReportsViewParamsComponent,
    ReportViewComponent,
    AppSettingsComponent,
    PracticeSettingsComponent,
    ApplicationConfigComponent,
    MaintainencePageComponent,
    GlobalMappingSettingComponent,
    TimelineComponent,
      ExportClinicalDataComponent,
      ApiKeysComponent,
      CatImageComponent,
      ImagesWrapperComponent,
      UserPreferenceSettingComponent,
      Time12HourPipe,

      AddInsuranceComponent,

      PatientDemographicComponent
  ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    //{ provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    BreadcrumbService,
    MessageService,
    DatePipe,
    NgxSpinnerService,
    ConfirmationService,
    Customsort,
    MenuService,
    PrivilegePipe,
    AuthGuard,
    DialogService,
    DynamicDialogRef
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
